import clsx from 'clsx';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import type { LineMovementSignal, GameOdds } from '../../types';
import { formatOdds } from '../../utils/format';

interface Props {
  lineMovement?: LineMovementSignal;
  odds?: GameOdds;
  awayTeamAbbr: string;
  homeTeamAbbr: string;
}

function MovementBadge({ pts, direction }: { pts: number; direction: 'away' | 'home' | 'mixed' | 'none' }) {
  if (pts === 0 || direction === 'none') {
    return <span className="flex items-center gap-1 text-xs text-gray-500"><Minus className="w-3 h-3" /> Flat</span>;
  }
  const positive = direction === 'away';
  return (
    <span className={clsx(
      'flex items-center gap-1 text-xs font-mono font-semibold',
      positive ? 'text-win' : 'text-loss',
    )}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : '-'}{pts}pts
    </span>
  );
}

export function MarketMicrostructure({ lineMovement, odds, awayTeamAbbr, homeTeamAbbr }: Props) {
  const hasSteam = lineMovement?.steamMove?.detected;

  return (
    <div className="card p-4 space-y-4">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        <Activity className="w-3 h-3" />
        Market Microstructure
      </div>

      {/* Current odds snapshot */}
      {odds && (
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-surface-4">
          <div className="space-y-2">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">{awayTeamAbbr} Lines</div>
            {odds.awayMoneyline != null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">ML</span>
                <span className={clsx('font-mono font-semibold', odds.awayMoneyline > 0 ? 'text-win' : 'text-gray-300')}>
                  {formatOdds(odds.awayMoneyline)}
                </span>
              </div>
            )}
            {odds.awayRunline != null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">RL</span>
                <span className="font-mono text-gray-300">{formatOdds(odds.awayRunline)}</span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">{homeTeamAbbr} Lines</div>
            {odds.homeMoneyline != null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">ML</span>
                <span className={clsx('font-mono font-semibold', odds.homeMoneyline > 0 ? 'text-win' : 'text-gray-300')}>
                  {formatOdds(odds.homeMoneyline)}
                </span>
              </div>
            )}
            {odds.homeRunline != null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">RL</span>
                <span className="font-mono text-gray-300">{formatOdds(odds.homeRunline)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {odds?.total && (
        <div className="flex items-center gap-2 text-xs border-t border-surface-4 pt-3">
          <span className="text-gray-500">O/U</span>
          <span className="font-mono font-semibold text-gray-200">{odds.total}</span>
          {odds.overOdds && <span className="text-gray-500 font-mono">O {formatOdds(odds.overOdds)}</span>}
          {odds.underOdds && <span className="text-gray-500 font-mono">U {formatOdds(odds.underOdds)}</span>}
        </div>
      )}

      {/* Line movement signals */}
      {lineMovement && (
        <div className="space-y-3 pt-3 border-t border-surface-4">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Line Movement</div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-gray-600 mb-1">Opening Drift</div>
              <div className="space-y-0.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{awayTeamAbbr}</span>
                  <MovementBadge
                    pts={Math.abs(lineMovement.openingDrift.awayPts)}
                    direction={lineMovement.openingDrift.awayPts > 0 ? 'away' : lineMovement.openingDrift.awayPts < 0 ? 'home' : 'none'}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{homeTeamAbbr}</span>
                  <MovementBadge
                    pts={Math.abs(lineMovement.openingDrift.homePts)}
                    direction={lineMovement.openingDrift.homePts > 0 ? 'away' : lineMovement.openingDrift.homePts < 0 ? 'home' : 'none'}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] text-gray-600 mb-1">Sharp Signal</div>
              <div className="text-sm font-mono font-semibold text-gray-200">
                {(lineMovement.sharpSignalStrength * 100).toFixed(0)}
                <span className="text-[10px] text-gray-500 ml-0.5 font-normal">/100</span>
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {lineMovement.consistency.dominantSide !== 'mixed'
                  ? `Pressure: ${lineMovement.consistency.dominantSide}`
                  : 'Mixed signals'}
              </div>
            </div>
          </div>

          {hasSteam && (
            <div className={clsx(
              'flex items-center gap-2 text-xs px-3 py-2 rounded',
              'bg-warn/10 border border-warn/20 text-warn',
            )}>
              <Activity className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">STEAM DETECTED</span>
              <span className="text-warn/70">· {lineMovement!.steamMove.direction} · {lineMovement!.steamMove.magnitudePts}pts</span>
            </div>
          )}

          {lineMovement.summary && (
            <div className="text-xs text-gray-500 leading-relaxed">{lineMovement.summary}</div>
          )}
        </div>
      )}

      {!lineMovement && !odds && (
        <div className="text-xs text-gray-600 py-2">Market data not available.</div>
      )}
    </div>
  );
}
